import { API_ENDPOINTS } from "@/configs";
import apiRequest from "@/lib/axios";
import { PlayStatement, RgLimitType, RgOverview, RgPeriod, RgSessionSummary } from "@/types";

// Fired after a settings change so the reality check picks up the new interval.
export const RG_CHANGED_EVENT = "rg:changed";

export const getRgOverview = async (): Promise<RgOverview> =>
  apiRequest({
    method: "GET",
    url: API_ENDPOINTS.RESPONSIBLE_GAMING.OVERVIEW,
    errorMessage: "Failed to load your responsible gambling settings",
  });

export const setRgLimit = async (data: {
  type: RgLimitType;
  period: RgPeriod;
  amount: number | null;
}): Promise<RgOverview> =>
  apiRequest({
    method: "PUT",
    url: API_ENDPOINTS.RESPONSIBLE_GAMING.LIMITS,
    data,
    errorMessage: "Failed to save the limit",
  });

export const setRgSessionSettings = async (data: {
  sessionLimitMinutes?: number | null;
  realityCheckMinutes?: number | null;
}): Promise<RgOverview> =>
  apiRequest({
    method: "PUT",
    url: API_ENDPOINTS.RESPONSIBLE_GAMING.SESSION,
    data,
    errorMessage: "Failed to save the session settings",
  });

export const startRgCoolOff = async (hours: number): Promise<RgOverview> =>
  apiRequest({
    method: "POST",
    url: API_ENDPOINTS.RESPONSIBLE_GAMING.COOL_OFF,
    data: { hours, confirm: true },
    errorMessage: "Failed to start the break",
  });

export const startRgSelfExclusion = async (months: number | "permanent"): Promise<RgOverview> =>
  apiRequest({
    method: "POST",
    url: API_ENDPOINTS.RESPONSIBLE_GAMING.SELF_EXCLUSION,
    data: { months, confirm: true },
    errorMessage: "Failed to start the self-exclusion",
  });

export const getRgSessionSummary = async (): Promise<RgSessionSummary> =>
  apiRequest({
    method: "GET",
    url: API_ENDPOINTS.RESPONSIBLE_GAMING.SESSION,
    errorMessage: "Failed to load your session",
  });

export const getPlayStatement = async (params: {
  from: string;
  to: string;
  currency?: string;
}): Promise<PlayStatement> =>
  apiRequest({
    method: "GET",
    url: API_ENDPOINTS.RESPONSIBLE_GAMING.STATEMENT,
    params,
    errorMessage: "Failed to load your statement",
  });

export const closeMyAccount = async (reason?: string): Promise<{ closedAt: string }> =>
  apiRequest({
    method: "POST",
    url: API_ENDPOINTS.ACCOUNT.CLOSE,
    data: { confirm: true, ...(reason ? { reason } : {}) },
    errorMessage: "Failed to close the account",
  });

/** Downloads the player's data export as a JSON file. */
export const downloadMyData = async (): Promise<void> => {
  const blob = await apiRequest<Blob>({
    method: "GET",
    url: API_ENDPOINTS.ACCOUNT.EXPORT,
    responseType: "blob",
    timeout: 120000,
    errorMessage: "Failed to prepare your data",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `my-data-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
